/**
 * Employee self-service privacy page.
 *
 * Two actions, both submit requests that HR reviews:
 *
 *  1. **Export my data** — request a JSON archive of every record tied to
 *     the caller. Synchronous on approval; downloadable for 7 days.
 *  2. **Request deletion** — anonymise my record after a 30-day grace
 *     window. HR can deny or cancel during the grace period.
 *
 * Also shows the caller's request history + status badges. Gives the
 * employee a clear paper trail of every LGPD/GDPR/CCPA interaction.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { pageTransition, formatDate } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { Download, ShieldAlert, Info } from "lucide-react";
import {
  downloadPrivacyExport,
  listMyPrivacyRequests,
  requestMyDeletion,
  requestMyExport,
  type PrivacyRequest,
  type PrivacyRequestStatus,
} from "@/api/privacy";

const STATUS_BADGE: Record<
  PrivacyRequestStatus,
  { variant: "success" | "warning" | "danger" | "default"; label: string }
> = {
  pending: { variant: "warning", label: "Pending review" },
  processing: { variant: "warning", label: "Scheduled" },
  completed: { variant: "success", label: "Completed" },
  denied: { variant: "danger", label: "Denied" },
  cancelled: { variant: "default", label: "Cancelled" },
};

export function MyPrivacyPage() {
  const { addToast } = useToast();
  const [requests, setRequests] = useState<PrivacyRequest[] | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      setRequests(await listMyPrivacyRequests());
    } catch {
      addToast("error", "Failed to load privacy requests");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasOpen = (type: "export" | "deletion") =>
    (requests || []).some(
      (r) => r.request_type === type && (r.status === "pending" || r.status === "processing")
    );

  const submitExport = async () => {
    setSaving(true);
    try {
      await requestMyExport(reason.trim() || undefined);
      addToast("success", "Export request submitted — HR will review shortly.");
      setExportOpen(false);
      setReason("");
      await reload();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Could not submit request");
    } finally {
      setSaving(false);
    }
  };

  const submitDeletion = async () => {
    setSaving(true);
    try {
      await requestMyDeletion(reason.trim() || undefined);
      addToast(
        "success",
        "Deletion request submitted — HR will review. You have 30 days to change your mind after approval."
      );
      setDeleteOpen(false);
      setReason("");
      await reload();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Could not submit request");
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async (r: PrivacyRequest) => {
    try {
      await downloadPrivacyExport(r.id, `my-data-${r.id.slice(0, 8)}.json`);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Download unavailable");
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Privacy & My Data"
        subtitle="Export or delete your personal information (LGPD / GDPR / CCPA)"
      />

      {/* ─── Actions ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-accent" />
              Download my data
            </CardTitle>
          </CardHeader>
          <p className="text-sm text-text-muted mb-4">
            Receive a JSON archive containing every record associated with
            you: profile, employment history, leave, time entries, documents,
            complaints, notifications, and audit trail. Approved requests
            generate a download link that stays valid for 7 days.
          </p>
          <div className="flex justify-end">
            <Button
              onClick={() => setExportOpen(true)}
              disabled={hasOpen("export")}
              title={
                hasOpen("export")
                  ? "You already have an open export request"
                  : undefined
              }
            >
              Request export
            </Button>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-danger">
              <ShieldAlert className="h-5 w-5" />
              Request data deletion
            </CardTitle>
          </CardHeader>
          <p className="text-sm text-text-muted mb-4">
            Anonymise your personal information. HR reviews the request
            first. Once approved, there's a 30-day grace window before the
            erasure runs — you or HR can cancel in that period. Employment
            records (hire date, department) are retained for aggregate
            reporting per LGPD Art. 16.
          </p>
          <div className="flex justify-end">
            <Button
              variant="danger"
              onClick={() => setDeleteOpen(true)}
              disabled={hasOpen("deletion")}
              title={
                hasOpen("deletion")
                  ? "You already have an open deletion request"
                  : undefined
              }
            >
              Request deletion
            </Button>
          </div>
        </Card>
      </div>

      {/* ─── History ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>My requests</CardTitle>
        </CardHeader>
        {!requests ? (
          <p className="text-sm text-text-muted">Loading…</p>
        ) : requests.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Info className="h-4 w-4" />
            You haven't submitted any privacy requests yet.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {requests.map((r) => {
              const badge = STATUS_BADGE[r.status];
              return (
                <div key={r.id} className="py-3 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary capitalize">
                        {r.request_type}
                      </span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-text-muted mt-0.5">
                      Submitted {formatDate(r.created_at)}
                      {r.scheduled_for &&
                        ` · Scheduled for ${formatDate(r.scheduled_for)}`}
                      {r.expires_at &&
                        ` · Download expires ${formatDate(r.expires_at)}`}
                    </p>
                    {r.admin_notes && (
                      <p className="text-xs text-text-muted mt-1 italic">
                        HR: {r.admin_notes}
                      </p>
                    )}
                  </div>
                  {r.has_download && (
                    <Button size="sm" variant="secondary" onClick={() => onDownload(r)}>
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ─── Export modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={exportOpen}
        onClose={() => !saving && setExportOpen(false)}
        title="Request data export"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            HR will review and generate your export. Optionally add a note
            for them below.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Optional: why do you need this export?"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setExportOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submitExport} isLoading={saving}>
              Submit request
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Delete modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => !saving && setDeleteOpen(false)}
        title="Request data deletion"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-text-primary">
            <p className="font-medium">This is a serious request.</p>
            <p className="text-xs text-text-muted mt-1">
              If approved, your profile will be anonymised after a 30-day
              grace window. Your login will be deactivated and you'll lose
              access to the application. Employment records required for
              legal compliance (hire date, termination date, department) are
              kept but your name and contact information are erased.
            </p>
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Optional: reason for the request"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDeletion} isLoading={saving}>
              Submit deletion request
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
